import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { ThumbsUp, ThumbsDown, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export const LEAD_REASONS = [
  { id: "wrong_industry", label: "Wrong industry" },
  { id: "wrong_size", label: "Wrong company size" },
  { id: "wrong_persona", label: "Wrong persona" },
  { id: "already_customer", label: "Already a customer" },
  { id: "competitor_vendor", label: "Competitor / vendor" },
];

export const SUGGESTION_REASONS = [
  { id: "not_relevant", label: "Not relevant to my ICP" },
  { id: "wrong_accounts", label: "Wrong accounts" },
  { id: "already_doing", label: "Already doing this" },
  { id: "too_generic", label: "Too generic" },
];

/**
 * Thumbs up / down that the system actually learns from. With `reasons`, a thumbs-down asks WHY (one tap) and the backend
 * turns it into exclusions / exemplars / source precision; the toast echoes what changed.
 */
const FeedbackButtons = ({
  userId,
  agentType,
  actionId = null,
  outputSummary = null,
  context = null,
  reasons = null,
  size = "sm",
  className = "",
  testIdPrefix = "",
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // 'up' or 'down'
  const [askingReason, setAskingReason] = useState(false);
  const [learned, setLearned] = useState([]);
  const tid = (s) => (testIdPrefix ? `${testIdPrefix}-${s}` : s);

  const send = async (rating, reason = null) => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/feedback`, {
        userId,
        agentType,
        rating, // 1 = thumbs down, 2 = thumbs up
        actionId,
        outputSummary: outputSummary?.substring(0, 500),
        context: { ...(context || {}), ...(reason ? { reason } : {}) },
      });
      const changes = r.data?.learned || [];
      setLearned(changes);
      setSubmitted(rating === 2 ? "up" : "down");
      setAskingReason(false);
      toast.success(changes[0] || (rating === 2 ? "Thanks for the positive feedback!" : "Thanks, we'll improve!"));
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFeedback = (rating) => {
    if (submitted || submitting) return;
    if (rating === 1 && reasons?.length) {
      setAskingReason(true);
      return;
    }
    send(rating);
  };

  const isLarge = size === "default" || size === "lg";
  const buttonSize = isLarge ? "h-9 w-9" : "h-7 w-7";
  const iconSize = isLarge ? "h-5 w-5" : "h-4 w-4";

  if (submitted) {
    return (
      <div className={`flex items-center gap-2 ${className}`} data-testid={tid("feedback-submitted")}>
        <span className={`${isLarge ? "text-sm" : "text-xs"} text-gray-400`} title={learned.join(" · ")}>
          {submitted === "up" ? "Thanks!" : learned[0] ? "Learned" : "Noted!"}
        </span>
        {submitted === "up" ? (
          <ThumbsUp className={`${iconSize} text-green-500 fill-green-500`} />
        ) : (
          <ThumbsDown className={`${iconSize} text-red-500 fill-red-500`} />
        )}
      </div>
    );
  }

  if (askingReason) {
    return (
      <Popover open onOpenChange={(open) => !open && setAskingReason(false)}>
        <PopoverAnchor asChild>
          <div className={`flex items-center gap-1 ${className}`}>
            <ThumbsDown className={`${iconSize} text-red-400`} />
          </div>
        </PopoverAnchor>
        <PopoverContent align="start" sideOffset={6} onClick={(e) => e.stopPropagation()}
          className="w-56 p-2 bg-[#0f1419] border-white/10 shadow-xl" data-testid={tid("feedback-reasons")}>
          <p className="px-2 pb-1.5 text-[11px] uppercase tracking-wider text-gray-500">Why wasn't this useful?</p>
          <div className="flex flex-col">
            {reasons.map((r) => (
              <button key={r.id} type="button" disabled={submitting} onClick={() => send(1, r.id)}
                className="w-full rounded-md px-2 py-1.5 text-left text-sm text-gray-200 hover:bg-red-500/10 hover:text-red-300 transition-colors disabled:opacity-50"
                data-testid={tid(`feedback-reason-${r.id}`)}>
                {r.label}
              </button>
            ))}
          </div>
          <button type="button" disabled={submitting} onClick={() => send(1)}
            className="mt-1 w-full rounded-md px-2 py-1 text-left text-xs text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-colors"
            data-testid={tid("feedback-reason-skip")}>
            Skip — just mark as not useful
          </button>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${className}`} data-testid={tid("feedback-buttons")}>
      <Button
        onClick={() => handleFeedback(2)}
        disabled={submitting}
        size={size}
        variant="ghost"
        className={`${buttonSize} p-0 text-gray-400 hover:text-green-500 hover:bg-green-500/10 transition-colors`}
        title="Good result"
        data-testid={tid("thumbs-up-btn")}
      >
        {submitting ? <Loader2 className={`${iconSize} animate-spin`} /> : <ThumbsUp className={iconSize} />}
      </Button>
      <Button
        onClick={() => handleFeedback(1)}
        disabled={submitting}
        size={size}
        variant="ghost"
        className={`${buttonSize} p-0 text-gray-400 hover:text-red-500 hover:bg-red-500/10 transition-colors`}
        title="Needs improvement"
        data-testid={tid("thumbs-down-btn")}
      >
        {submitting ? <Loader2 className={`${iconSize} animate-spin`} /> : <ThumbsDown className={iconSize} />}
      </Button>
    </div>
  );
};

export default FeedbackButtons;
